// src/pages/battle/Battlemap.jsx
import React, { useEffect } from 'react';
import { THEMES } from '../themeConfig';
import { UnitType } from '../../utils/combatSystem';
import {
  useBattleSetup,
  useBattleState,
  useBattleActions,
  useEnemyTurn,
  useVictoryCheck,
  useAfterActionNavigation
} from './BattleMap.hooks';
import { getUnitsByType } from './Battlemap.utils';
import BattleHeader from './components/BattleHeader';
import SquadStatus from './components/SquadStatus';
import HostilesPanel from './components/HostilesPanel';
import BattleGrid from './components/BattleGrid';
import CombatLog from './components/CombatLog';
import GameOverScreen from './components/GameOverScreen';
import ActionControls from './components/ActionControls';
// NEW: Import sprite system
import { preloadBattleSprites } from './components/PixelSpriteIntegration';
import './components/PixelSprite.css';

const ThemedBattlemap = ({ theme = 'vanilla' }) => {
  const { navigate, location, activeTheme, activeMission, activeSquad, campaignId } = useBattleSetup(theme);
  
  const config = THEMES[activeTheme] || THEMES.vanilla;
  const primaryColor = config.colors.primary;
  const alertColor = config.alert.color;
  
  const battleState = useBattleState(activeMission, activeSquad);
  const {
    map,
    units,
    setUnits,
    selectedUnit,
    setSelectedUnit,
    mode,
    setMode,
    selectedAbility,
    setSelectedAbility,
    turn,
    turnCount,
    log,
    addLog,
    gameOver,
    setGameOver,
    victory,
    setVictory
  } = battleState;

  const {
    endTurn,
    handleAbilitySelect,
    handleTileClick,
    getUnitAt
  } = useBattleActions(battleState, map);

  const soldiers = getUnitsByType(units, UnitType.SOLDIER);
  const enemies = getUnitsByType(units, UnitType.ENEMY);

  const navigateToAfterAction = useAfterActionNavigation(
    navigate,
    location,
    activeTheme,
    activeMission,
    activeSquad,
    units,
    victory,
    turnCount,
    campaignId
  );

  const abortMission = () => {
    if (window.confirm('Are you sure you want to abort the mission?')) {
      setGameOver(true);
      setVictory(false);
      addLog('Mission aborted by commander');
    }
  };

  // NEW: Preload sprites when battle starts
  useEffect(() => {
    preloadBattleSprites(activeTheme, activeSquad);
  }, [activeTheme, activeSquad]);

  // Enemy turn processing - FIXED: Added setUnits parameter
  useEnemyTurn(turn, units, setUnits, gameOver, endTurn, map, addLog);
  
  // Victory condition checking
  useVictoryCheck(units, gameOver, setGameOver, setVictory, addLog);

  return (
    <div className={`min-h-screen bg-gradient-to-b ${config.colors.background} text-white`}>
      <BattleHeader
        activeMission={activeMission}
        turn={turn}
        turnCount={turnCount}
        gameOver={gameOver}
        endTurn={endTurn}
        abortMission={abortMission}
        alertColor={alertColor}
        primaryColor={primaryColor}
      />

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid lg:grid-cols-[260px_1fr_340px] gap-4 items-start">
          {/* Left Panel */}
          <div className="space-y-4 w-full overflow-hidden">
            <SquadStatus 
              soldiers={soldiers} 
              primaryColor={primaryColor} 
              alertColor={alertColor}
              theme={activeTheme}
            />
            <HostilesPanel 
              enemies={enemies} 
              alertColor={alertColor}
              theme={activeTheme}
            />
          </div>

          {/* Center Battle Area */}
          <div className="flex flex-col gap-4 min-w-0">
            {gameOver ? (
              <GameOverScreen
                victory={victory}
                activeMission={activeMission}
                activeSquad={activeSquad}
                soldiers={soldiers}
                turnCount={turnCount}
                navigateToAfterAction={navigateToAfterAction}
                alertColor={alertColor}
                primaryColor={primaryColor}
              />
            ) : (
              <>
                <BattleGrid
                  map={map}
                  units={units}
                  selectedUnit={selectedUnit}
                  turn={turn}
                  handleTileClick={handleTileClick}
                  getUnitAt={getUnitAt}
                  primaryColor={primaryColor}
                  alertColor={alertColor}
                  theme={activeTheme}
                />
                <CombatLog log={log} />
              </>
            )}
          </div>

          {/* Right Panel */}
          <div className="space-y-4 w-full max-w-full overflow-hidden">
            {selectedUnit && turn === UnitType.SOLDIER && !gameOver && (
              <div className="w-full overflow-hidden">
                <ActionControls
                  selectedUnit={selectedUnit}
                  setSelectedUnit={setSelectedUnit}
                  mode={mode}
                  setMode={setMode}
                  selectedAbility={selectedAbility}
                  setSelectedAbility={setSelectedAbility}
                  handleAbilitySelect={handleAbilitySelect}
                  primaryColor={primaryColor}
                  alertColor={alertColor}
                  theme={activeTheme}
                />
              </div>
            )}

            {!selectedUnit && turn === UnitType.SOLDIER && !gameOver && (
              <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
                <p className="text-sm text-gray-400 text-center">
                  Select a unit to view available actions
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export const VanillaBattlemap = (props) => <ThemedBattlemap theme="vanilla" {...props} />;
export const MajesticBattlemap = (props) => <ThemedBattlemap theme="majestic" {...props} />;
export const DevastatorsBattlemap = (props) => <ThemedBattlemap theme="devastators" {...props} />;
export const ArchboundBattlemap = (props) => <ThemedBattlemap theme="archbound" {...props} />;

export default ThemedBattlemap;